import {CommentDiscussionIcon, RocketIcon} from '@primer/octicons-react'
import {
  checksStatusPresentation,
  conflictsStatusPresentation,
  reviewStatusPresentation,
  type CondensedStatusRow,
} from '../../helpers/condensed-merge-status-presentation'
import {useMergeboxStatusCalculator} from '../../hooks/use-mergebox-status-calculator'
import {EMPTY_CHANNELS, type MergeabilityLiveUpdateChannels} from '../../hooks/use-mergeability-live-updates'
import {useUnresolvedConversationCount} from '@github-ui/pull-requests/layout-selectors'
import {isConversationResolutionRequired} from '../../helpers/conversation-resolution'
import {useValidatedMergeData} from './use-validated-merge-data'

export function useMergeStatusRows(): CondensedStatusRow[] | null {
  const validatedMergeData = useValidatedMergeData()

  // hack: the hooks below were not built with undefined/null in mind
  const headSha = validatedMergeData?.headSha ?? ''
  const channels: MergeabilityLiveUpdateChannels = validatedMergeData?.channels ?? EMPTY_CHANNELS

  const {calculator, statusChecksData, mergeRequirements} = useMergeboxStatusCalculator({headSha, channels})
  const unresolvedConversationCount = useUnresolvedConversationCount() ?? 0

  if (!validatedMergeData) return null

  const rows: CondensedStatusRow[] = []
  const {ChecksSection, ConflictsSection, ReviewerSection, BlockedSection} = calculator.sections

  if (ChecksSection.shouldRender) {
    rows.push(checksStatusPresentation(ChecksSection, statusChecksData))
  }

  if (ConflictsSection.shouldRender) {
    rows.push(conflictsStatusPresentation(ConflictsSection))
  }

  if (ReviewerSection.shouldRender) {
    rows.push(reviewStatusPresentation(ReviewerSection))
  }

  if (unresolvedConversationCount > 0 && isConversationResolutionRequired(mergeRequirements)) {
    rows.push({
      icon: CommentDiscussionIcon,
      iconColor: 'var(--fgColor-attention)',
      label: `${unresolvedConversationCount} unresolved conversation${unresolvedConversationCount === 1 ? '' : 's'}`,
    })
  }

  const deploymentBlocked = BlockedSection.failingConditionsAndRules.some(condition =>
    condition.displayName.toLowerCase().includes('deployment'),
  )

  if (deploymentBlocked) {
    rows.push({
      icon: RocketIcon,
      iconColor: 'var(--fgColor-attention)',
      label: 'Deployment required',
    })
  }

  return rows
}
